import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import axios from "axios";
import { API_URL } from "config";
import SoftSelect from "components/SoftSelect";
import { useAuth } from "../../../AuthContext";

const parseStoredPermissions = () => {
  try {
    const stored = localStorage.getItem("permissions");
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

function AddShift() {
  const navigate = useNavigate();
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();

  const [formData, setFormData] = useState({
    branch_id: "",
    subbranch_id: "",
    type: "day",
    start_time: "",
    end_time: "",
    has_flexible_break: true,
    break_duration: "60",
    grace_time: "",
    expected_hours: "480",
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [subBranches, setSubBranches] = useState([]);
  const [timeError, setTimeError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    color: "success",
    icon: "check",
  });

  const grantedPermissions = useMemo(
    () => Array.from(new Set([...permissions, ...userPermission, ...parseStoredPermissions()])),
    [permissions, userPermission]
  );

  const hasShiftPermission = useCallback(
    (...requiredPermissions) => {
      if (grantedPermissions.includes("access.system.full_access")) return true;
      return requiredPermissions
        .flat()
        .filter(Boolean)
        .some((permission) => grantedPermissions.includes(permission));
    },
    [grantedPermissions]
  );

  const canViewPage = hasShiftPermission("shift.pages.create.view");
  const canSubmit =
    hasShiftPermission("shift.actions.create") &&
    hasShiftPermission("shift.actions.submit_create");
  const canOpenList =
    hasShiftPermission("shift.pages.list.view") &&
    hasShiftPermission("shift.actions.open_list");
  const canOpenTrashed =
    hasShiftPermission("shift.pages.trashed.view") &&
    hasShiftPermission("shift.actions.open_trashed");
  const canViewField = useCallback(
    (field) => hasShiftPermission(`shift.fields.${field}.view`),
    [hasShiftPermission]
  );

  useEffect(() => {
    if (!isAuthReady) return;

    if (!canViewPage || !canViewField("branch")) {
      setPageLoading(false);
      return;
    }

    const fetchBranches = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await axios.get(`${API_URL}/shifts/branch-options`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        setBranches(res.data?.data || []);
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to load branches",
          color: "error",
          icon: "error",
        });
      } finally {
        setPageLoading(false);
      }
    };

    fetchBranches();
  }, [canViewField, canViewPage, isAuthReady]);

  useEffect(() => {
    const fetchSubBranches = async () => {
      if (!formData.branch_id || !canViewField("sub_branch")) {
        setSubBranches([]);
        return;
      }

      try {
        const token = localStorage.getItem("authToken");
        const res = await axios.get(`${API_URL}/shifts/sub-branch-options/${formData.branch_id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        setSubBranches(res.data?.data || []);
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to load sub branches",
          color: "error",
          icon: "error",
        });
      }
    };

    fetchSubBranches();
  }, [canViewField, formData.branch_id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target || {};
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  useEffect(() => {
    const { start_time, end_time, break_duration, expected_hours } = formData;

    if (!start_time || !end_time || !expected_hours || !break_duration) {
      setTimeError("");
      return;
    }

    const [startH, startM] = start_time.split(":").map(Number);
    const [endH, endM] = end_time.split(":").map(Number);

    let start = startH * 60 + startM;
    let end = endH * 60 + endM;

    if (end < start) end += 24 * 60;

    const breakMins = parseInt(break_duration || "0", 10);
    const expectedMins = parseInt(expected_hours || "0", 10);
    const actualWorkingMins = end - start - breakMins;

    if (breakMins !== 60) {
      setTimeError("Break duration must be exactly 60 minutes.");
    } else if (actualWorkingMins !== expectedMins) {
      setTimeError(
        `Expected working minutes must equal shift duration - break. Currently ${actualWorkingMins} mins, but expected is ${expectedMins} mins.`
      );
    } else {
      setTimeError("");
    }
  }, [
    formData.start_time,
    formData.end_time,
    formData.break_duration,
    formData.expected_hours,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit) {
      setSnackbar({
        open: true,
        message: "You do not have permission to create shifts.",
        color: "error",
        icon: "error",
      });
      return;
    }

    if (!formData.branch_id || !formData.subbranch_id) {
      setSnackbar({
        open: true,
        message: "Branch and Sub Branch are mandatory",
        color: "error",
        icon: "error",
      });
      return;
    }

    setLoading(true);
    const token = localStorage.getItem("authToken");
    const { start_time, end_time, break_duration, expected_hours } = formData;
    const [startH, startM] = start_time.split(":").map(Number);
    const [endH, endM] = end_time.split(":").map(Number);
    let start = startH * 60 + startM;
    let end = endH * 60 + endM;

    if (end < start) end += 24 * 60;

    const breakMins = parseInt(break_duration || "0", 10);
    const expectedMins = parseInt(expected_hours || "0", 10);
    const actualWorkingMins = end - start - breakMins;

    if (breakMins !== 60 || actualWorkingMins !== expectedMins) {
      setSnackbar({
        open: true,
        message: `Break must be 60 mins and working duration = expected (${expectedMins} mins).`,
        color: "error",
        icon: "error",
      });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/shifts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          branch_id: formData.branch_id,
          subbranch_id: formData.subbranch_id,
          type: formData.type,
          start_time: formData.start_time,
          end_time: formData.end_time,
          has_flexible_break: formData.has_flexible_break,
          break_duration: formData.break_duration,
          grace_time: formData.grace_time,
          expected_hours: formData.expected_hours,
          is_active: formData.is_active,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create shift.");
      }

      setSnackbar({
        open: true,
        message: "Shift created successfully!",
        color: "success",
        icon: "check",
      });
      setTimeout(() => navigate("/shifts"), 1500);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to create shift.",
        color: "error",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  if (!isAuthReady || pageLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Add Shift
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to add shifts.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  const branchOptions = branches.map((branch) => ({ value: branch.id, label: branch.name }));
  const subBranchOptions = subBranches.map((subBranch) => ({
    value: subBranch.id,
    label: subBranch.name,
  }));
  const shiftTypeOptions = [
    { value: "day", label: "Day" },
    { value: "night", label: "Night" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h4">Add Shift</SoftTypography>
          <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {canOpenList && (
              <SoftButton color="info" onClick={() => navigate("/shifts")} startIcon={<Icon>list</Icon>}>
                Shift List
              </SoftButton>
            )}
            {canOpenTrashed && (
              <SoftButton
                color="dark"
                variant="outlined"
                onClick={() => navigate("/shifts/trashed")}
                startIcon={<Icon>delete</Icon>}
              >
                Trashed List
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>

        <SoftBox maxWidth="600px" ml="0">
          <form onSubmit={handleSubmit}>
            {canViewField("branch") && (
              <SoftBox mb={2}>
                <SoftSelect
                  size="small"
                  placeholder="Select Branch"
                  options={branchOptions}
                  value={branchOptions.find((option) => option.value === formData.branch_id) || null}
                  onChange={(option) =>
                    setFormData((prev) => ({
                      ...prev,
                      branch_id: option ? option.value : "",
                      subbranch_id: "",
                    }))
                  }
                />
              </SoftBox>
            )}

            {canViewField("sub_branch") && (
              <SoftBox mb={2}>
                <SoftSelect
                  size="small"
                  placeholder="Select Sub Branch"
                  options={subBranchOptions}
                  value={subBranchOptions.find((option) => option.value === formData.subbranch_id) || null}
                  onChange={(option) =>
                    setFormData((prev) => ({
                      ...prev,
                      subbranch_id: option ? option.value : "",
                    }))
                  }
                />
              </SoftBox>
            )}

            {canViewField("type") && (
              <SoftBox mb={2}>
                <SoftSelect
                  placeholder="Select Shift Type"
                  size="small"
                  options={shiftTypeOptions}
                  value={shiftTypeOptions.find((option) => option.value === formData.type)}
                  onChange={(option) =>
                    setFormData((prev) => ({
                      ...prev,
                      type: option ? option.value : "day",
                    }))
                  }
                />
              </SoftBox>
            )}

            {(canViewField("start_time") || canViewField("end_time")) && (
              <SoftBox mb={2} display="flex" gap={2}>
                {canViewField("start_time") && (
                  <SoftInput
                    size="small"
                    name="start_time"
                    type="time"
                    value={formData.start_time}
                    onChange={handleChange}
                    required
                  />
                )}
                {canViewField("end_time") && (
                  <SoftInput
                    name="end_time"
                    type="time"
                    value={formData.end_time}
                    onChange={handleChange}
                    required
                    size="small"
                  />
                )}
              </SoftBox>
            )}
            {timeError && (
              <SoftTypography variant="caption" color="error" mb={2}>
                {timeError}
              </SoftTypography>
            )}

            {canViewField("break_duration") && (
              <SoftBox mb={2}>
                <SoftInput
                  name="break_duration"
                  type="number"
                  placeholder="Break Duration (must be 60 mins)"
                  value={formData.break_duration}
                  onChange={handleChange}
                  size="small"
                />
              </SoftBox>
            )}

            {canViewField("has_flexible_break") && (
              <SoftBox mb={2} display="flex" alignItems="center">
                <Switch
                  checked={formData.has_flexible_break}
                  onChange={handleChange}
                  name="has_flexible_break"
                />
                <SoftTypography variant="button" ml={1}>
                  Flexible Break
                </SoftTypography>
              </SoftBox>
            )}

            {canViewField("grace_time") && (
              <SoftBox mb={2}>
                <SoftInput
                  size="small"
                  name="grace_time"
                  placeholder="Grace Time (in minutes)"
                  value={formData.grace_time}
                  onChange={handleChange}
                />
              </SoftBox>
            )}

            {canViewField("expected_hours") && (
              <input type="hidden" name="expected_hours" value={formData.expected_hours} />
            )}

            {canViewField("is_active") && (
              <SoftBox mb={2} display="flex" alignItems="center">
                <Switch checked={formData.is_active} onChange={handleChange} name="is_active" />
                <SoftTypography variant="button" ml={1}>
                  Active
                </SoftTypography>
              </SoftBox>
            )}

            {canSubmit && (
              <SoftBox mt={3}>
                <SoftButton
                  size="small"
                  type="submit"
                  color="info"
                  fullWidth
                  disabled={loading || !!timeError}
                  endIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {loading ? "Processing..." : "Create Shift"}
                </SoftButton>
              </SoftBox>
            )}
          </form>
        </SoftBox>
      </SoftBox>

      <SoftSnackbar
        color={snackbar.color}
        icon={snackbar.icon || "notifications"}
        title="Shift"
        content={snackbar.message}
        open={snackbar.open}
        close={closeSnackbar}
        onClose={closeSnackbar}
      />
    </DashboardLayout>
  );
}

export default AddShift;
